import axios from 'axios'
import type {
  BiologyCategory,
  AIModel,
  Literature,
  Question,
  SearchLiteratureResponse,
  GenerateQuestionsResponse,
  ConvertLatexResponse
} from '@shared/types'

// 创建 axios 实例
const api = axios.create({
  baseURL: import.meta.env.PROD
    ? 'https://ibo-question-maker-backend.peungsun.workers.dev/api'
    : 'http://localhost:8787/api',
  timeout: 120000, // 增加到 120 秒，因为 AI 调用可能需要较长时间
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response) {
      // 服务器返回错误
      const message = error.response.data?.message || error.response.statusText
      throw new Error(message)
    } else if (error.request) {
      // 请求发出但没有收到响应
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('请求超时，AI 处理时间较长，请稍后重试')
      } else if (error.code === 'ERR_NETWORK') {
        throw new Error('无法连接到服务器，请检查网络连接')
      } else {
        throw new Error('网络请求失败，请检查网络连接或稍后重试')
      }
    } else {
      // 其他错误
      throw new Error(error.message || '未知错误')
    }
  }
)

/**
 * 文献检索 API
 */
export async function searchLiterature(
  category: BiologyCategory,
  keyword: string,
  apiKey: string,
  model: AIModel,
  excludeLiteratures?: Array<{ title: string; authors?: string; year?: number }>
): Promise<Literature[]> {
  const response = await api.post<SearchLiteratureResponse>('/search-literature', {
    category,
    keyword,
    apiKey,
    model,
    excludeLiteratures
  })

  return response.data.literatures
}

/**
 * 题目生成 API
 */
export async function generateQuestions(
  literature: Literature,
  apiKey: string,
  model: AIModel
): Promise<Question[]> {
  const response = await api.post<GenerateQuestionsResponse>('/generate-questions', {
    literature,
    apiKey,
    model
  })

  return response.data.questions
}

/**
 * LaTeX 转换 API
 */
export async function convertToLatex(
  questions: Question[]
): Promise<string> {
  const response = await api.post<ConvertLatexResponse>('/convert-latex', {
    questions
  })

  return response.data.latex
}

/**
 * 测试 API 配置是否正确
 * @param apiKey - 可选，如果不提供则测试后台默认配置
 * @param model - AI 模型类型
 */
export async function testApiKey(
  apiKey: string | undefined,
  model: AIModel
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await api.post('/test-api', {
      apiKey,
      model
    })

    return {
      success: true,
      message: response.data.message || 'API 配置正确'
    }
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'API 测试失败'
    }
  }
}

/**
 * 获取后端分析数据
 */
export interface Analytics {
  totalRequests: number
  successfulRequests: number
  failedRequests: number
  endpointStats: Record<string, number>
  modelStats: Record<string, number>
  recentLogs: Array<{
    timestamp: string
    method: string
    path: string
    status: number
    duration: number
    model?: string
    error?: string
  }>
  startTime: string
}

export async function getAnalytics(): Promise<Analytics> {
  const response = await api.get('/analytics')
  return response.data.data
}

/**
 * 重置分析数据
 */
export async function resetAnalytics(): Promise<void> {
  await api.delete('/analytics')
}

/**
 * 翻译题目
 */
export async function translateQuestions(
  questions: Question[],
  targetLanguage: 'zh-CN' | 'zh-TW' | 'en',
  apiKey: string,
  model: AIModel
): Promise<Question[]> {
  const response = await api.post('/translate-questions', {
    questions,
    targetLanguage,
    apiKey,
    model
  })

  return response.data.questions
}

/**
 * 保存题目到历史记录
 */
export async function saveQuestionsToHistory(
  questions: Question[]
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await api.post('/questions/save', { questions })
    return {
      success: true,
      message: response.data.message || '保存成功'
    }
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : '保存失败'
    }
  }
}

/**
 * 根据ID获取题目
 */
export async function getQuestionById(id: string): Promise<Question | null> {
  try {
    const response = await api.get(`/questions/${id}`)
    return response.data.question
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * 搜索题目
 */
export interface SearchQuestionsParams {
  keyword?: string
  difficulty?: string
  knowledgePoint?: string
  literatureName?: string
  dateFrom?: string
  dateTo?: string
  limit?: number
  offset?: number
}

export interface SearchQuestionsResult {
  questions: Question[]
  total: number
  limit: number
  offset: number
}

export async function searchQuestions(
  params: SearchQuestionsParams
): Promise<SearchQuestionsResult> {
  const response = await api.post('/questions/search', params)
  return response.data
}

/**
 * 获取历史统计信息
 */
export interface HistoryStats {
  total: number
  byDifficulty: {
    easy: number
    medium: number
    hard: number
  }
  knowledgePoints: string[]
  literatures: string[]
}

export async function getHistoryStats(): Promise<HistoryStats> {
  const response = await api.get('/questions/stats')
  return response.data
}
